import React from "react";

// Constants
import {
    EVERYDAY_PRICE_SNAPSHOT,
    ICKONIC_PRICE_SOURCE,
    ONS_VALUE_COMPARISON_PERIOD,
    ONS_VALUE_COMPARISON_SOURCE,
    PriceSourceSnapshot,
    STREAMING_PRICE_SNAPSHOT,
    VALUE_COMPARISON_CHECKED_LABEL,
} from "../../../Constants/FrontValueComparisonSnapshot";

// Helpers
import { formatMoney } from "../../../Helpers/Currency/Currency";
import { selectMonthlyMarketingPlan } from "../../../Helpers/PlanPricing";

// Models
import PlanDTO from "../../../Models/DTOs/Plan";

interface FrontValueComparisonSectionProps {
    plans: PlanDTO[] | null;
    plansLoading: boolean;
    plansError: string;
}

interface SourceReferenceProps {
    provider: string;
    source: PriceSourceSnapshot;
}

const SourceReference: React.FC<SourceReferenceProps> = ({
    provider,
    source,
}) => (
    <p className="front-value-comparison__source-reference">
        <a
            aria-label={`Open the official ${provider} price source`}
            href={source.sourceUrl}
            rel="noopener noreferrer"
            target="_blank"
        >
            {source.sourceLabel} source
        </a>
        <span aria-hidden="true">&middot;</span>
        <span>
            Checked{" "}
            <time dateTime={source.checkedDate}>
                {VALUE_COMPARISON_CHECKED_LABEL}
            </time>
        </span>
    </p>
);

const FrontValueComparisonSection: React.FC<
    FrontValueComparisonSectionProps
> = ({ plans, plansLoading, plansError }) => {
    const monthlyPlan = selectMonthlyMarketingPlan(plans);
    const normalizedCurrency = monthlyPlan?.currency?.trim().toUpperCase();
    const hasPlansError = Boolean(plansError.trim());
    const hasUsableBasePrice =
        !plansLoading &&
        !hasPlansError &&
        normalizedCurrency === "GBP" &&
        typeof monthlyPlan?.price === "number" &&
        Number.isSafeInteger(monthlyPlan.price) &&
        monthlyPlan.price > 0;
    const monthlyBasePrice = hasUsableBasePrice
        ? formatMoney(monthlyPlan.price, "GBP")
        : null;
    const comparisonIsAvailable = Boolean(monthlyBasePrice && monthlyPlan);

    const heading = (
        <div className="front-value-comparison__heading">
            <p className="front-value-comparison__kicker">
                A small commitment, only ~27p/$0.36 per day
            </p>
            <h2 id="front-value-comparison-heading">
                A month of Ickonic.
                <span>Put into perspective.</span>
            </h2>
        </div>
    );

    if (!comparisonIsAvailable || !monthlyPlan || !monthlyBasePrice) {
        return (
            <section
                id="value-comparison"
                className="front-value-comparison"
                aria-busy={plansLoading}
                aria-labelledby="front-value-comparison-heading"
                tabIndex={-1}
            >
                <div className="front-value-comparison__inner">
                    {heading}
                    <div
                        className="front-value-comparison__unavailable"
                        role="status"
                    >
                        <strong>
                            {plansLoading
                                ? "Loading the current value comparison\u2026"
                                : "The current price comparison is temporarily unavailable."}
                        </strong>
                        <span>
                            It will return when the latest standard membership
                            price has been confirmed.
                        </span>
                    </div>
                </div>
            </section>
        );
    }

    const isBelowSelectedGbpStreamingPrices = STREAMING_PRICE_SNAPSHOT.filter(
        (snapshot) => snapshot.currency === "GBP"
    ).every((snapshot) => monthlyPlan.price < snapshot.priceMinor);
    const isBelowEverydayPrices = EVERYDAY_PRICE_SNAPSHOT.every(
        (snapshot) => monthlyPlan.price < snapshot.priceMinor
    );

    return (
        <section
            id="value-comparison"
            className="front-value-comparison"
            aria-busy="false"
            aria-labelledby="front-value-comparison-heading"
            tabIndex={-1}
        >
            <div className="front-value-comparison__inner">
                <div className="front-value-comparison__introduction">
                    {heading}
                    <div className="front-value-comparison__lead">
                        <p>
                            {isBelowSelectedGbpStreamingPrices ? (
                                <>
                                    On price alone, Ickonic&apos;s standard{" "}
                                    <strong>{monthlyBasePrice}/month</strong>{" "}
                                    membership sits below the selected UK
                                    streaming tiers.
                                </>
                            ) : (
                                <>
                                    See Ickonic&apos;s standard{" "}
                                    <strong>{monthlyBasePrice}/month</strong>{" "}
                                    membership alongside selected streaming
                                    tiers.
                                </>
                            )}{" "}
                            Every platform offers a different library and set
                            of features.
                        </p>
                    </div>
                </div>

                <div className="front-value-comparison__panels">
                    <article className="front-value-comparison__streaming">
                        <header>
                            <p>Streaming subscriptions</p>
                            <h3>How the monthly price compares</h3>
                        </header>

                        <ul aria-label="Selected standard monthly streaming prices">
                            <li className="front-value-comparison__streaming-item front-value-comparison__streaming-item--ickonic">
                                <div className="front-value-comparison__provider">
                                    <strong>Ickonic</strong>
                                    <span>Monthly membership</span>
                                </div>
                                <p className="front-value-comparison__price">
                                    <strong>{monthlyBasePrice}</strong>
                                    <span>/month</span>
                                </p>
                                <SourceReference
                                    provider="Ickonic"
                                    source={ICKONIC_PRICE_SOURCE}
                                />
                            </li>

                            {STREAMING_PRICE_SNAPSHOT.map((snapshot) => (
                                <li
                                    className="front-value-comparison__streaming-item"
                                    key={snapshot.id}
                                >
                                    <div className="front-value-comparison__provider">
                                        <strong>{snapshot.provider}</strong>
                                        <span>{snapshot.plan}</span>
                                    </div>
                                    <p className="front-value-comparison__price">
                                        <strong>
                                            {formatMoney(
                                                snapshot.priceMinor,
                                                snapshot.currency
                                            )}
                                        </strong>
                                        <span>{snapshot.priceSuffix}</span>
                                    </p>
                                    <SourceReference
                                        provider={snapshot.provider}
                                        source={snapshot}
                                    />
                                </li>
                            ))}
                        </ul>

                        <p className="front-value-comparison__qualification">
                            Selected standard rolling monthly prices are shown.
                            Introductory, annual, student, bundled and fixed-term
                            offers are excluded. Netflix and Disney+ offer
                            lower-priced ad-supported plans; Disney+ also
                            advertises a fixed-term saver offer. Libraries,
                            features, supported devices and simultaneous streams
                            differ. Gaia lists its price in US dollars plus
                            applicable tax and has not been converted.
                        </p>
                    </article>

                    <article className="front-value-comparison__everyday">
                        <header>
                            <p>Everyday perspective</p>
                            <h3>
                                Less than the things we barely think twice
                                about.
                            </h3>
                            <p>
                                {isBelowEverydayPrices ? (
                                    <>
                                        At {monthlyBasePrice}, one month costs
                                        less than each of these current UK
                                        averages.
                                    </>
                                ) : (
                                    <>
                                        Put {monthlyBasePrice} alongside these
                                        current UK averages.
                                    </>
                                )}
                            </p>
                        </header>

                        <ul aria-label={`${ONS_VALUE_COMPARISON_PERIOD} UK average prices`}>
                            {EVERYDAY_PRICE_SNAPSHOT.map((snapshot, index) => (
                                <li key={snapshot.id}>
                                    <span aria-hidden="true">
                                        {String(index + 1).padStart(2, "0")}
                                    </span>
                                    <div>
                                        <strong>{snapshot.label}</strong>
                                        <span>{snapshot.detail}</span>
                                    </div>
                                    <strong>
                                        {formatMoney(
                                            snapshot.priceMinor,
                                            "GBP"
                                        )}
                                    </strong>
                                </li>
                            ))}
                        </ul>

                        <div className="front-value-comparison__ons-source">
                            <p>
                                {ONS_VALUE_COMPARISON_PERIOD} UK averages;
                                actual prices vary.
                            </p>
                            <SourceReference
                                provider="ONS"
                                source={ONS_VALUE_COMPARISON_SOURCE}
                            />
                        </div>
                    </article>
                </div>

                <p className="front-value-comparison__legal">
                    Provider names are trademarks of their respective owners.
                </p>
            </div>
        </section>
    );
};

export default FrontValueComparisonSection;
